import { useAccessRight, useTranslate } from "@/hooks";
import { Button, Slide } from "@mui/material";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import styles from "./CallingLineLogs.module.scss";
import cn from "classnames";
import { BlockWithError, BlockWithInfo, CustomSelect, CustomTextField } from "@/ui";
import { useGetCallingListLogsQuery, useGetCallingListsQuery } from "@/store/RTKQuery/clsSlice";
import { ICallingListLogs } from "./CallingLineLogs.props";
import { ILogItem } from "@/types/clsTypes";
import { ColumnDef, FilterFn, getCoreRowModel, getPaginationRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";
import Table from "@/components/Tables/Table/Table";
import TableFooter from "@/components/Tables/TableFooter/TableFooter";
import { useCookies } from "react-cookie";
import { CLS } from "@/constants/accessRights";
import NoticeSingleAction from "@/components/MUI/NoticeSingleAction/NoticeSingleAction";

const CallingListLogs: FC<ICallingListLogs> = ({ showLogs, setShowLogs }) => {
	const [cookies] = useCookies(["cls"]);
	const [currentList, setCurrentList] = useState<{ value: string; title: string } | undefined>(
		cookies.cls?.currentLogsList ? JSON.parse(cookies.cls.currentLogsList) : undefined
	);
	const tableContainerRef = useRef<HTMLDivElement>(null);

	const translate = useTranslate(); // hook для перевода текста
	const isAccess = useAccessRight();

	const [showTestScriptBlock] = useState<boolean>(false); // показ блока тестирования скрипта
	const [callingLists, setCallingLists] = useState<{ value: string; title: string }[]>([]);
	const [snackbarMessage, setSnackbarMessage] = useState("");
	const [showNotification, setShowNotification] = useState(false);
	// локальный стейт для даты
	const [dateFrom, setDateFrom] = useState<string>(() => new Date().toISOString().split("T")[0]);

	const { data, isLoading, refetch } = useGetCallingListsQuery(
		{},
		{
			refetchOnMountOrArgChange: false,
			refetchOnReconnect: false,
		}
	);
	const { data: logs, refetch: refetchLogs } = useGetCallingListLogsQuery({ id: currentList?.value || "", date: dateFrom });
	useEffect(() => {
		if (cookies?.cls?.currentLogsList) {
			const savedList = cookies.cls ? JSON.parse(cookies.cls.displayMode).selectedCallList : "";

			const currentCallingList = callingLists.find((el) => el.value === savedList);

			if (currentCallingList) {
				setCurrentList(currentCallingList);
			}
		}
	}, []);

	useEffect(() => {
		if (cookies?.cls) {
			const savedList = cookies.cls ? JSON.parse(cookies.cls.displayMode).selectedCallList : "";

			const currentCallingList = callingLists.find((el) => el.value === savedList);

			if (currentCallingList) {
				setCurrentList(currentCallingList);
			}
		}
	}, [callingLists, cookies.cls]);

	const columns = useMemo<ColumnDef<ILogItem>[]>(
		() => [
			{
				accessorKey: "destination",
				header: translate("title_abonent"),
				cell: (info) => info.row.original.destination,
			},
			{
				accessorKey: "status",
				header: translate("title_status"),
			},
			{
				accessorKey: "duration",
				header: translate("title_duration"),
			},
			{
				accessorKey: "hangup_cause",
				header: translate("title_finishReason"),
			},

			{
				accessorKey: "log_created_date",
				header: translate("title_time"),
				cell: (info) => {
					const raw = info.getValue() as string;
					const cleaned = raw.replace(/%f/, "000"); // заменяем некорректную часть
					const date = new Date(cleaned);
					return date.toLocaleString("ru-RU", {
						hour: "2-digit",
						minute: "2-digit",
					});
				},
			},
		],
		[]
	);

	const dummyFilter: FilterFn<any> = () => true;
	const tableData = useMemo(() => (logs?.logs ? logs.logs.flat() : []), [logs?.logs]);

	const table = useReactTable<ILogItem>({
		data: tableData,
		columns,
		initialState: {
			pagination: { pageIndex: 0, pageSize: 100 }, // начальное состояние
		},
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getSortedRowModel: getSortedRowModel(),
		filterFns: {
			myCustomFilter: dummyFilter, // обязателен, но не используется
		},
	});

	useEffect(() => {
		if (data && !isLoading && data.error !== 1) {
			const sortedLists = [...data.lists].sort((a, b) => a.name.localeCompare(b.name));

			setCallingLists(
				sortedLists.map((list) => ({
					value: list.id,
					title: list.name,
				}))
			);
		}
	}, [data]);

	const closeHandler = (): void => {
		setShowLogs(false);
	};

	const handleShowNotice = (message: string) => {
		setSnackbarMessage(message);
		setShowNotification(true);
	};

	const handleRefetchLogs = () => {
		handleShowNotice("notice_logsUpdating");
		refetchLogs()
			.unwrap()
			.then((res) => {
				if (res.error === 0) handleShowNotice("notice_logsUpdated");
				else handleShowNotice(`${translate("title_error")}: ${res.message}`);
			})
			.catch((err) => handleShowNotice(`${translate("title_error")}: ${JSON.stringify(err)}`));
	};

	const handleChangeCurrentList = (listId: string) => {
		if (callingLists.length === 0) return;

		const selected = callingLists.find((l) => l.value === listId);
		if (!selected) return;

		setCurrentList(selected);
	};

	return (
		<>
			<Slide direction="left" in={showLogs} mountOnEnter unmountOnExit>
				<div className={styles.modal} onMouseDown={closeHandler}>
					<div
						className={cn(styles.container, {
							[styles.containerFullWidth]: showTestScriptBlock,
						})}
						onMouseDown={(e) => e.stopPropagation()}
						ref={tableContainerRef}
					>
						{data?.error === 1 ? (
							<BlockWithError updateHandler={refetch} />
						) : (
							<>
								{isAccess(CLS.LISTS_LIST) && isAccess(CLS.LOGS) && (
									<div className={cn(styles["logs-params"])}>
										<CustomSelect
											variantsList={callingLists}
											title={translate("selectItem_callingList")}
											setState={handleChangeCurrentList}
											elementWidth={"full"}
											valueIndex={currentList ? callingLists.findIndex((el) => el.value === currentList.value) ?? 0 : 0}
											sx={{ mb: 16 }}
											MenuProps={{
												container: tableContainerRef.current,
												disablePortal: true,
												sx: { zIndex: 9999 },
											}}
										/>
										<CustomTextField
											type={"date"}
											name={"date"}
											sx={{ width: "100%", mt: "4px" }}
											label={translate("title_logDateFrom")}
											height={40}
											value={dateFrom}
											onChange={(e) => setDateFrom(e.target.value)}
											InputLabelProps={{ shrink: true }}
											required
										/>
										<Button
											variant="outlined"
											sx={{
												fontSize: 11,
												width: "25%",
												mt: "4px",
												textWrap: "nowrap",
												alignItems: "center",
												display: "flex",
												whiteSpace: "nowrap",
												overflow: "hidden",
												textOverflow: "ellipsis",
											}}
											onClick={handleRefetchLogs}
										>
											{translate("button_update")}
										</Button>
									</div>
								)}
								<div className={cn(styles["table-container"])}>
									{isAccess(CLS.LOGS) ? (
										<Table<ILogItem>
											table={table}
											tableContainerRef={tableContainerRef}
											withFilters={false} // фильтра нет
											columnsWidth={
												<colgroup>
													<col style={{ width: "9%" }} />
													<col style={{ width: "8%" }} />
													<col style={{ width: "11%" }} />
													<col style={{ width: "17%" }} />
													<col style={{ width: "8%" }} />
												</colgroup>
											}
										/>
									) : (
										<BlockWithInfo title={translate("title_noAccessRights")} />
									)}
								</div>
								{isAccess(CLS.LOGS) && <TableFooter<ILogItem> table={table} tableContainerRef={tableContainerRef} />}
							</>
						)}

						<div className={styles.tagClose} onClick={closeHandler}>
							{translate("button_close")}
						</div>
					</div>
				</div>
			</Slide>
			<NoticeSingleAction
				severity={snackbarMessage.toLowerCase().includes("error") || snackbarMessage.toLowerCase().includes("ошибка") ? "error" : "info"}
				showNotification={showNotification}
				setShowNotification={setShowNotification}
				title={snackbarMessage}
			/>
		</>
	);
};

export default CallingListLogs;
